/**
 * feat-256 — the date vocabulary for a Lesson's optional release instant
 * (`learn_lesson.available_at`).
 *
 * Design authority: `design/feat-256-notes.md` §2, which is normative. Every
 * format string below is quoted from §7's copy table by number.
 *
 * ONE `Intl.DateTimeFormat` per format, in the viewer's own locale and timezone —
 * the same pattern `formatContentTimestamp` (`ContentTimestamps.tsx`) already
 * uses. The viewer's zone is deliberate: the picker operates in the admin's own
 * browser timezone and labels it on screen, while the value on the wire is always
 * UTC. Every learner on the tenant flips at the same instant (notes §2.4).
 *
 * WHY A SEPARATE MODULE. The conversion in `localInputToUtcIso` is the one piece
 * of this feature a test can get wrong in a way that stays green everywhere:
 * spec AC24 forbids recomputing the expected UTC string with the same `Intl`
 * options the component uses, because such an assertion is correct in EVERY
 * timezone and can therefore detect nothing. Keeping the conversion here, free of
 * React, lets the TZ-pinned arms call it directly against hand-typed literals.
 */

/** notes §2.1 — the future/`Held ·` instant. `timeZoneName` is NOT optional (§2.4). */
const SCHEDULE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/** notes §2.2 — the released chip's abbreviated date. No time, no zone, no weekday. */
const RELEASED_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/**
 * notes §7.17 / §7.18 — the precise instant that lives in a chip's `title`.
 *
 * This is `SCHEDULE_FORMAT` plus the year. It exists because of the title rule in
 * notes §3.4, which is binding on every chip in this feature:
 *
 *   > a `title` may carry PRECISION the visible text abbreviates; it may NEVER
 *   > carry MEANING the visible text omits.
 *
 * The full instant behind `Available since Aug 20` is precision, so a tooltip is
 * the right home for it. "Why nothing happens on Wednesday" is meaning, and it
 * goes on screen in the popover instead (string 7.12) — a `title` needs a hover,
 * does not exist on touch, and is announced inconsistently by screen readers.
 */
const PRECISE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/**
 * Format `ms` under `options`, dropping the comma `Intl` emits after the weekday.
 *
 * `Intl` renders `SCHEDULE_FORMAT` as `Wed, Aug 26, 9:00 AM PDT`; the target
 * string (7.2) has no comma there and keeps the later ones. A blanket
 * comma-strip would eat those too, so this rewrites exactly the ONE literal part
 * that directly follows the `weekday` part. `formatToParts` is also what makes
 * AC25 cheap, since a test can assert per part instead of phrase-matching across
 * a line that may have wrapped.
 *
 * ⚠️ THE LITERAL IS REPLACED WITH A SPACE, NOT DROPPED — and that one character
 * is a correction to design notes §2.1's sample code, which `.filter()`s the part
 * out entirely. `Intl` emits `", "` (comma AND space) as a SINGLE literal part,
 * so filtering it removes the separator along with the comma and yields
 * `WedAug 26, 9:00 AM PDT`. The notes' own target string, 7.2, has the space, so
 * the copy table and the sample code disagree by one character and the copy
 * table is the thing that is normative. Caught by the assertion in
 * `lessonSchedule.losAngeles.feat256.test.ts`, which is why that test states the
 * whole string rather than only "no leading `Wed,`". Reported to `main`.
 */
function formatWithoutWeekdayComma(ms: number, options: Intl.DateTimeFormatOptions): string {
  const parts = new Intl.DateTimeFormat(undefined, options).formatToParts(new Date(ms));
  return parts
    .map((p, i) => (p.type === 'literal' && parts[i - 1]?.type === 'weekday' ? ' ' : p.value))
    .join('')
    .trim();
}

/**
 * notes §2.1 / §2.3 — `Wed Aug 26, 9:00 AM PDT`.
 *
 * Returns `null` for anything unparseable, and EVERY caller must fall back to the
 * empty affordance (`＋ Schedule`) rather than render it. Never `Invalid Date` —
 * notes §7.3, the same rule `formatContentTimestamp` follows (feat-252 AC-F3).
 */
export function formatReleaseInstant(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return formatWithoutWeekdayComma(ms, SCHEDULE_FORMAT);
}

/**
 * notes §2.2 — `Aug 20`, or `Aug 20, 2025` when the year is not the current one.
 *
 * The year is appended rather than always shown because this string is history
 * and the precision would be noise — but WITHOUT it a year-old release reads as
 * last week, which is the one misreading this format can produce. The full
 * instant stays reachable in the chip's `title` (7.18), so nothing is lost.
 */
export function formatReleasedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const then = new Date(ms);
  const sameYear = then.getFullYear() === new Date().getFullYear();
  return then.toLocaleDateString(undefined, sameYear ? RELEASED_FORMAT : { ...RELEASED_FORMAT, year: 'numeric' });
}

/** notes §7.17 / §7.18 — `Wed Aug 26, 2026, 9:00 AM PDT`. Precision for a `title`. */
export function formatPreciseInstant(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return formatWithoutWeekdayComma(ms, PRECISE_FORMAT);
}

/** notes §2.4 — the IANA zone for the popover's long-form timezone sentence (7.9). */
export function resolvedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** notes §2.4 — the short zone abbreviation (`PDT`, `GMT+9`) for the same sentence. */
export function shortTimeZoneName(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(at);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

const LOCAL_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * `<input type="datetime-local">`'s value → the ISO-8601 UTC string the API takes.
 *
 * **This is the function spec AC29 mutates.** Reverting it to return `value`
 * unchanged sends the admin's LOCAL wall time to a column stored in naive UTC,
 * and every Lesson releases at the wrong instant by the viewer's offset. In the
 * author's own timezone the wrong answer and the right one can look alike in a
 * display assertion, which is exactly why AC24 requires two pinned TZ arms and a
 * hand-typed expected literal.
 *
 * The components are read out of the string and handed to the `Date(y, m, d, …)`
 * constructor, which interprets them as LOCAL time by definition. That is chosen
 * over `new Date(value)` deliberately: the single-argument string parse is
 * local-vs-UTC depending on whether the string carries a time part, a distinction
 * that is easy to break later by "normalising" the input format.
 *
 * Returns `null` when the field is empty or malformed, so a caller never sends
 * `Invalid Date` to the wire.
 */
export function localInputToUtcIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = LOCAL_INPUT_RE.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, min, s] = m;
  const local = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(min), Number(s ?? 0), 0);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

/**
 * The inverse — an ISO-8601 UTC string → the `datetime-local` field's value, so
 * reopening the editor shows the instant the admin actually chose, in their zone.
 *
 * Built from the LOCAL getters rather than by slicing the ISO string: slicing
 * would hand the field a UTC wall time and the popover would disagree with the
 * chip sitting directly above it.
 */
export function utcIsoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Has this instant already passed?
 *
 * `null`/unparseable is NOT released — it is "no schedule at all", which the
 * callers render as the empty affordance. Keeping the NaN case here means the
 * chip variants never have to re-ask.
 */
export function isReleased(iso: string | null | undefined, now: number = Date.now()): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return ms <= now;
}

/**
 * Does this Lesson show the third tree-pill state, `Later`? — i.e. it is Active
 * but held back until an instant that has not arrived yet.
 *
 * ONE definition, three call sites (`MasterDetailTree`, `LessonLibraryPane`,
 * `TrackDetailPane`). Spec AC27 requires all three to AGREE, and three inlined
 * copies of `active && available_at && Date.parse(...) > Date.now()` is exactly
 * how three surfaces come to disagree — the same reasoning that puts the backend's
 * availability rule in one `lesson_available_sql`.
 *
 * This is NOT the thing design notes §5.5 forbids hoisting. That prohibition is
 * on `LESSON_STATUS_PILL`, the per-file CLASS constant, and on merging the Lesson
 * pill into the Track-header pill: they share a token pair, which is not the same
 * as sharing a component. A predicate over the data is a different object
 * entirely, and each site keeps its own markup, its own shape classes and its own
 * `min-w` decision.
 *
 * An UNPARSEABLE `available_at` is not `Later` — it falls back to plain `On`,
 * matching the meta strip, which falls back to the empty affordance rather than
 * render `Invalid Date`. Note this is why the check cannot be written as
 * `!isReleased(iso)`: that is `true` for garbage as well as for the future.
 */
export function isScheduledLater(
  unit: { active?: boolean; available_at?: string | null },
  now: number = Date.now(),
): boolean {
  if (!unit.active) return false;
  if (!unit.available_at) return false;
  const ms = Date.parse(unit.available_at);
  if (Number.isNaN(ms)) return false;
  return ms > now;
}

/* ───────────────────────────────────────────────────────────────────────────
 * feat-256 followup — collision-aware placement for the Release date popover.
 *
 * WHY THIS IS HERE AND NOT INLINE IN THE COMPONENT: jsdom has no layout engine,
 * so every `getBoundingClientRect` is zeros and a clamp test written against the
 * component asserts arithmetic on zeros and passes NO MATTER WHAT THE CODE DOES.
 * That is the same blind spot that let the clipping bug ship — the popover was
 * unreachable on the real page while the suite was green. Extracting the
 * arithmetic is the only part of this that can be honestly tested today, and it
 * is where the errors actually live.
 *
 * THE RULE THIS ENCODES (design r5 §4): stay LEFT-ALIGNED to the anchor and
 * SHIFT back inside the clipping box by exactly the overflow. Do not pick a
 * side. `left-0` clips at every measured width except 1152; `right-0` clips at
 * 1152 and fits at 1280 — the failing side FLIPS between two adjacent laptop
 * widths, so no static side is correct. A shift is continuous: it moves the
 * panel by exactly the overflow and no more, so the panel still reads as
 * belonging to its chip.
 *
 * This is `shift()` from Floating UI, hand-rolled. Do not add the dependency for
 * one popover.
 * ─────────────────────────────────────────────────────────────────────────── */

/** The horizontal slice of a `DOMRect` that placement actually needs. */
export interface Span {
  left: number;
  right: number;
}

/** Design r5 §4. Breathing room between the panel and the pane edge. */
export const POPOVER_GUTTER = 8;

/** Design r5 §4/§5. The panel's preferred width; it only ever shrinks. */
export const POPOVER_MAX_WIDTH = 276;

const spanWidth = (s: Span): number => s.right - s.left;

/**
 * The panel's width: its preference, capped so it fits the clipping box with a
 * gutter on both sides (design r5 §5, approved relaxation).
 *
 * Below roughly a 1008px window NO placement strategy fits — the pane is 290px
 * at a 1000px window and 250px at 960px, and 960px is simply a half-screen
 * window on a 1920 display, so it is reachable rather than theoretical.
 * Shrinking to 234px keeps the input, both notes and the Clear/Done row usable;
 * clipping blocks the user outright. Shrinking is the better failure.
 *
 * AN UNMEASURABLE CLIP BOX YIELDS THE PREFERRED WIDTH, not a negative one. In
 * jsdom every rect is zeros, and `0 - 16` would render a panel -16px wide; a
 * real browser can also hand back zeros for a subtree that is not laid out yet.
 * Degrading to the natural width is the only safe answer in both cases.
 */
export function popoverWidth({
  clip,
  maxWidth = POPOVER_MAX_WIDTH,
  gutter = POPOVER_GUTTER,
}: {
  clip: Span;
  maxWidth?: number;
  gutter?: number;
}): number {
  const available = spanWidth(clip) - 2 * gutter;
  if (!Number.isFinite(available) || available <= 0) return maxWidth;
  return Math.min(maxWidth, available);
}

/**
 * The panel's left edge, in the same coordinate space as the rects passed in.
 *
 * Preference is `anchor.left` — unchanged from the original design, and what
 * keeps the panel tethered to the control that opened it. It is then clamped
 * into `[clip.left + gutter, clip.right - width - gutter]`.
 *
 * THE MAX IS APPLIED BEFORE THE MIN, and the order matters when the panel is
 * wider than the box: `Math.min(Math.max(...))` then pins the panel to the
 * box's RIGHT edge, hiding its left. Callers should size with `popoverWidth`
 * first so the range is never inverted — but the clamp stays correct rather
 * than exploding if they do not.
 *
 * An unmeasurable clip box leaves the anchor alone (shift 0), for the same
 * reason `popoverWidth` returns its preference: better to render where the old
 * code rendered than to teleport the panel to zero.
 */
export function clampPopoverLeft({
  anchor,
  clip,
  width,
  gutter = POPOVER_GUTTER,
}: {
  anchor: Span;
  clip: Span;
  width: number;
  gutter?: number;
}): number {
  if (spanWidth(clip) <= 0) return anchor.left;
  const min = clip.left + gutter;
  const max = clip.right - width - gutter;
  // Degenerate box (panel wider than the space even after sizing): prefer
  // showing the panel's LEFT edge, since that is where its label and input are.
  if (max < min) return min;
  return Math.min(Math.max(anchor.left, min), max);
}

/**
 * Both halves together, in the offset form the component applies.
 *
 * `offset` is relative to the anchor because the panel is `absolute` inside the
 * anchor's `relative` wrapper — so `left: offset` is what actually lands it at
 * the clamped absolute position.
 */
export function resolvePopoverPlacement({
  anchor,
  clip,
  maxWidth = POPOVER_MAX_WIDTH,
  gutter = POPOVER_GUTTER,
}: {
  anchor: Span;
  clip: Span;
  maxWidth?: number;
  gutter?: number;
}): { width: number; offset: number } {
  const width = popoverWidth({ clip, maxWidth, gutter });
  const left = clampPopoverLeft({ anchor, clip, width, gutter });
  return { width, offset: left - anchor.left };
}
